import { useCallback } from 'react';
import { getHotelApiToken } from '../../../core/auth/getHotelApiToken';
import type { HotelSearchBatchPayload, HotelSearchQuery } from '../api/hotels.types';
import { hotelSearchStreamUrl } from '../api/hotels.endpoints';
import { HOTEL_REQUEST_TIMEOUTS_MS } from '../api/requestTimeouts';
import type { HotelOfferCardModel } from '../types/hotels.presentation';
import { mapHotelSearchRequest } from '../utils/mapHotelSearchRequest';
import { mapStreamOfferToCard } from '../utils/mapStreamOfferToCard';
import { parseSseChunk } from '../utils/sseStreamParser';

const DEFAULT_RETRY_AFTER_MS = 5_000;
const MAX_RETRY_AFTER_MS = 5_000;

export interface HotelSearchStreamCallbacks {
  onBatch: (cards: HotelOfferCardModel[], payload: HotelSearchBatchPayload) => void;
  onDone: () => void;
}

export interface UseHotelSearchStreamOptions {
  fetchFn?: typeof fetch;
  getTokenFn?: typeof getHotelApiToken;
  waitForRetry?: (ms: number, signal: AbortSignal) => Promise<void>;
  /** Longest silence (no response, no event) before giving up. Default 60 s. */
  idleTimeoutMs?: number;
}

export class HotelSearchStreamError extends Error {
  messageKey: string;

  constructor(messageKey: string, detail?: string) {
    super(detail ?? messageKey);
    this.name = 'HotelSearchStreamError';
    this.messageKey = messageKey;
  }
}

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError';
}

/** Resolves `promise` or rejects with a search timeout error after `ms` without settling. */
function withIdleTimeout<T>(promise: Promise<T>, ms: number, onTimeout: () => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      onTimeout();
      reject(new HotelSearchStreamError('hotels.results.searchError', 'hotel_search_idle_timeout'));
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function retryAfterToMs(value: string | null) {
  if (!value) return DEFAULT_RETRY_AFTER_MS;

  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return DEFAULT_RETRY_AFTER_MS;

  return Math.min(seconds * 1_000, MAX_RETRY_AFTER_MS);
}

function defaultWaitForRetry(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(resolve, ms);

    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timeout);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

export function useHotelSearchStream(options?: UseHotelSearchStreamOptions) {
  const searchStream = useCallback(
    async (query: HotelSearchQuery, callbacks: HotelSearchStreamCallbacks, signal: AbortSignal) => {
      const fetcher = options?.fetchFn ?? fetch;
      const tokenResolver = options?.getTokenFn ?? getHotelApiToken;
      const waitForRetry = options?.waitForRetry ?? defaultWaitForRetry;
      const idleTimeoutMs = options?.idleTimeoutMs ?? HOTEL_REQUEST_TIMEOUTS_MS.searchIdle;
      const token = await tokenResolver();

      if (!token) {
        throw new HotelSearchStreamError('hotels.results.authError');
      }

      const request = mapHotelSearchRequest({
        destinationId: query.destinationId ?? '',
        countryCode: query.countryCode ?? '',
        checkIn: query.checkIn ?? '',
        checkOut: query.checkOut ?? '',
        adults: query.adults ?? 1,
        children: query.children,
        childrenAges: query.childrenAges,
        rooms: query.rooms,
        stars: query.stars,
      });

      const response = await fetchWithBoundedRetry({
        fetcher,
        token,
        body: JSON.stringify(request),
        signal,
        waitForRetry,
        idleTimeoutMs,
      });

      await consumeHotelStream(response, callbacks, signal, query.destination, idleTimeoutMs);
    },
    [options?.fetchFn, options?.getTokenFn, options?.waitForRetry, options?.idleTimeoutMs],
  );

  return { searchStream };
}

async function fetchWithBoundedRetry({
  fetcher,
  token,
  body,
  signal,
  waitForRetry,
  idleTimeoutMs,
}: {
  fetcher: typeof fetch;
  token: string;
  body: string;
  signal: AbortSignal;
  waitForRetry: (ms: number, signal: AbortSignal) => Promise<void>;
  idleTimeoutMs: number;
}) {
  const request = () => {
    // Own controller so a backend that never answers can be cut without aborting the search.
    // It follows the search signal for the whole request, body stream included.
    const controller = new AbortController();
    signal.addEventListener('abort', () => controller.abort(), { once: true });
    return withIdleTimeout(
      fetcher(hotelSearchStreamUrl(), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body,
        signal: controller.signal,
      }),
      idleTimeoutMs,
      () => controller.abort(),
    );
  };

  let response = await request();

  if (response.status === 503) {
    await waitForRetry(retryAfterToMs(response.headers.get('Retry-After')), signal);
    response = await request();
  }

  if (!response.ok) {
    throw new HotelSearchStreamError(
      'hotels.results.searchError',
      `hotel_search_failed:${response.status}`,
    );
  }

  if (!response.body) {
    throw new HotelSearchStreamError('hotels.results.searchError', 'hotel_search_missing_body');
  }

  return response;
}

async function consumeHotelStream(
  response: Response,
  callbacks: HotelSearchStreamCallbacks,
  signal: AbortSignal,
  fallbackDestination?: string,
  idleTimeoutMs: number = HOTEL_REQUEST_TIMEOUTS_MS.searchIdle,
) {
  const reader = response.body?.getReader();
  if (!reader) throw new HotelSearchStreamError('hotels.results.searchError');

  const decoder = new TextDecoder();
  let buffer = '';
  let hasReceivedData = false;

  try {
    while (!signal.aborted) {
      // A stream that stays silent too long is cut: partial results still count (see catch).
      const { done, value } = await withIdleTimeout(reader.read(), idleTimeoutMs, () => {
        void reader.cancel().catch(() => undefined);
      });
      if (done) break;

      const parsed = parseSseChunk(decoder.decode(value, { stream: true }), buffer);
      buffer = parsed.remaining;

      for (const event of parsed.events) {
        if (event.event === 'done') {
          callbacks.onDone();
          return;
        }

        if (event.event !== 'hotel') continue;

        let payload: HotelSearchBatchPayload;
        try {
          payload = JSON.parse(event.data) as HotelSearchBatchPayload;
        } catch {
          // A malformed hotel event should not invalidate the entire stream.
          // Skip the event and continue; if a valid "done" arrives later,
          // the search completes successfully with whatever offers were already received.
          console.warn('Hotel search stream: skipping malformed hotel event', event.data);
          continue;
        }

        callbacks.onBatch(
          mapStreamOfferToCard(
            payload.list,
            payload.info,
            payload.searchToken,
            fallbackDestination,
          ),
          payload,
        );
        hasReceivedData = true;
      }
    }

    if (signal.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    callbacks.onDone();
  } catch (error) {
    if (isAbortError(error)) {
      // Aborted — do not propagate
    } else if (hasReceivedData) {
      // Stream transport error after partial data is not unrecoverable.
      // Treat as graceful completion so the user sees results instead of an error modal.
      console.warn('Hotel search stream ended with error after receiving data', error);
      callbacks.onDone();
    } else {
      throw error;
    }
  } finally {
    reader.releaseLock();
  }
}
